import type { WebContainer } from '@webcontainer/api';
import {
  WORKBENCH_FS_DEADLINE_MS,
  WORKBENCH_SPAWN_DEADLINE_MS,
  WorkbenchUnresponsiveError,
  withDeadline,
} from '../../webcontainer/deadline';

/**
 * Per-turn compile check through the Vite dev server that is already running, instead of a
 * separate `vite build` (which starts a second Vite, bundles every module and doubled the
 * memory peak of a build step: WC-6). Publish does not use the browser's `dist/` — Chef builds
 * the deploy from the saved files (`chef/app/lib/.server/prototyper/deploy.ts`).
 *
 * `VITE_DEV_CHECK_SCRIPT` runs with `node` inside the WebContainer. It requests `/src/main.tsx`
 * from the dev server, follows every `/src/...` import in the transformed code, and prints one
 * JSON line: `{"checked":N,"errors":[{url,message,file}]}`, or `{"unavailable":true,...}` when
 * the server cannot be reached. Vite answers a module it cannot transform (syntax error,
 * unresolved import, CSS/PostCSS failure) with HTTP 500 and the error embedded as
 * `const error = {...}`, which the script extracts.
 */

export const VITE_DEV_CHECK_SCRIPT_PATH = 'node_modules/.cache/ideoz/vite-dev-check.mjs';
const MAX_ERROR_CHARS = 2_000;

export const VITE_DEV_CHECK_SCRIPT = String.raw`import http from 'node:http';

const port = Number(process.argv[2] || 3111);
const entry = process.argv[3] || '/src/main.tsx';
const MAX_MODULES = 600;
const IMPORT_RE = /(?:\bfrom\s*|\bimport\s*\(?\s*)["'](\/src\/[^"'?#]+)(?:[?#][^"']*)?["']/g;

function get(path) {
  return new Promise((resolve) => {
    const req = http.get({ host: 'localhost', port, path, headers: { accept: '*/*' } }, (res) => {
      let body = '';
      res.setEncoding('utf8');
      res.on('data', (chunk) => { body += chunk; });
      res.on('end', () => resolve({ status: res.statusCode || 0, body }));
    });
    req.on('error', (err) => resolve({ status: 0, body: String((err && err.message) || err) }));
    req.setTimeout(30000, () => req.destroy(new Error('Request timed out: ' + path)));
  });
}

function errorFrom(body) {
  const match = body.match(/const error = (\{.*\})\r?\n/);
  if (match) {
    try {
      const err = JSON.parse(match[1]);
      const file = err.id || (err.loc && err.loc.file) || undefined;
      return { message: String(err.message || 'Transform failed').slice(0, ${MAX_ERROR_CHARS}), file };
    } catch {}
  }
  const text = body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return { message: (text || 'Transform failed').slice(0, ${MAX_ERROR_CHARS}) };
}

const seen = new Set();
const queue = [entry];
const errors = [];
while (queue.length > 0 && seen.size < MAX_MODULES) {
  const url = queue.shift();
  if (seen.has(url)) continue;
  seen.add(url);
  const { status, body } = await get(url);
  if (status === 0) {
    console.log(JSON.stringify({ unavailable: true, message: body }));
    process.exit(0);
  }
  if (status >= 400) {
    errors.push({ url, ...errorFrom(body) });
    continue;
  }
  for (const m of body.matchAll(IMPORT_RE)) {
    if (!seen.has(m[1])) queue.push(m[1]);
  }
}
console.log(JSON.stringify({ checked: seen.size, errors }));
`;

export interface ViteDevCheckError {
  url: string;
  message: string;
  file?: string;
}

export type ViteDevCheckResult =
  | { status: 'passed'; checked: number }
  | { status: 'failed'; checked: number; errors: ViteDevCheckError[] }
  /** The dev server could not be reached or the script did not report; fall back to `vite build`. */
  | { status: 'unavailable'; reason: string };

/** Reads the last JSON line the script printed. */
export function parseViteDevCheckOutput(stdout: string): ViteDevCheckResult {
  const lines = stdout
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i]!;
    if (!line.startsWith('{')) continue;
    try {
      const parsed = JSON.parse(line) as {
        unavailable?: boolean;
        message?: string;
        checked?: number;
        errors?: ViteDevCheckError[];
      };
      if (parsed.unavailable) {
        return { status: 'unavailable', reason: parsed.message ?? 'dev server unreachable' };
      }
      if (typeof parsed.checked === 'number' && Array.isArray(parsed.errors)) {
        return parsed.errors.length > 0
          ? { status: 'failed', checked: parsed.checked, errors: parsed.errors }
          : { status: 'passed', checked: parsed.checked };
      }
    } catch {
      /* not the result line */
    }
  }
  return { status: 'unavailable', reason: 'the check printed no result' };
}

/** `/home/project/src/App.tsx` → `src/App.tsx`, matching the paths the model writes. */
function workspacePath(file: string): string {
  return file.replace(/^.*?\/(src\/)/, '$1');
}

/** LLM-facing text, one block per failing module. */
export function formatViteDevCheckErrors(errors: readonly ViteDevCheckError[]): string {
  return errors
    .map((e) => {
      const where = e.file ? workspacePath(e.file) : e.url.replace(/^\//, '');
      return `${where}:\n${e.message.trim()}`;
    })
    .join('\n\n');
}

const DEV_CHECK_TIMEOUT_MS = 60_000;

export async function runViteDevCheck(
  wc: WebContainer,
  opts: { port: number; signal?: AbortSignal; onOutput?: (chunk: string) => void }
): Promise<ViteDevCheckResult> {
  try {
    await withDeadline(
      wc.fs.mkdir('node_modules/.cache/ideoz', { recursive: true }),
      WORKBENCH_FS_DEADLINE_MS,
      'Preparing the compile check'
    );
    await withDeadline(
      wc.fs.writeFile(VITE_DEV_CHECK_SCRIPT_PATH, VITE_DEV_CHECK_SCRIPT),
      WORKBENCH_FS_DEADLINE_MS,
      'Preparing the compile check'
    );
    const proc = await withDeadline(
      wc.spawn('node', [VITE_DEV_CHECK_SCRIPT_PATH, String(opts.port)]),
      WORKBENCH_SPAWN_DEADLINE_MS,
      'Starting the compile check'
    );
    const onAbort = () => void proc.kill();
    opts.signal?.addEventListener('abort', onAbort);

    let stdout = '';
    const piping = proc.output.pipeTo(
      new WritableStream<string>({
        write(chunk) {
          stdout += chunk;
          opts.onOutput?.(chunk);
        },
      })
    );

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<'timeout'>((resolve) => {
      timer = setTimeout(() => {
        void proc.kill();
        resolve('timeout');
      }, DEV_CHECK_TIMEOUT_MS);
    });
    const exit = await Promise.race([proc.exit, timedOut]);
    if (timer !== undefined) clearTimeout(timer);
    opts.signal?.removeEventListener('abort', onAbort);
    if (exit === 'timeout') return { status: 'unavailable', reason: 'the check timed out' };
    await piping.catch(() => undefined);
    return parseViteDevCheckOutput(stdout);
  } catch (err) {
    // A frozen workspace would fail the `vite build` fallback the same way; let the build stop.
    if (err instanceof WorkbenchUnresponsiveError) throw err;
    return {
      status: 'unavailable',
      reason: err instanceof Error ? err.message : String(err),
    };
  }
}
